import { Component, ElementRef, HostListener, effect, signal } from '@angular/core';

import { faArrowsRotate, faChevronRight, faCopy, faFileLines, faShieldHalved, faSparkles, faSpinnerThird, faTriangleExclamation, faXmark } from '@fortawesome/pro-solid-svg-icons';

import { AdminActionMenuAction, AdminActionMenuContext } from 'src/app/shared/models/admin-action-menu.model';
import { AdminActionMenuService } from 'src/app/shared/services/admin-action-menu.service';
import { AdminNotesFabService } from 'src/app/shared/services/admin-notes-fab.service';
import { AppVersionService } from 'src/app/shared/services/app-version.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { UserRolesService } from 'src/app/shared/services/user-roles.service';

type VersionCheckState = 'idle' | 'checking' | 'current' | 'stale' | 'error';

@Component({
    selector: 'app-admin-action-menu',
    templateUrl: './admin-action-menu.component.html',
    styleUrls: ['./admin-action-menu.component.scss'],
    standalone: false
})
export class AdminActionMenuComponent {
  private readonly mobileSheetHeightStorageKey = 'admin-action-menu.mobile-height';
  private readonly mobileSheetSnapPoints = [60, 75, 90];
  private readonly defaultMobileSheetHeight = 75;
  private readonly minMobileSheetHeight = 60;
  private readonly maxMobileSheetHeight = 90;
  private resizePointerId: number | null = null;
  private resizeStartY = 0;
  private resizeStartHeight = this.defaultMobileSheetHeight;

  faShieldHalved = faShieldHalved;
  faChevronRight = faChevronRight;
  faXmark = faXmark;
  faSparkles = faSparkles;
  faSpinnerThird = faSpinnerThird;
  faTriangleExclamation = faTriangleExclamation;
  faFileLines = faFileLines;
  faCopy = faCopy;
  faArrowsRotate = faArrowsRotate;
  readonly versionCheck = signal<VersionCheckState>('idle');
  readonly visibleSections = this.adminActionMenuService.visibleSections;
  readonly hasVisibleActions = this.adminActionMenuService.hasVisibleActions;
  readonly actionCount = this.adminActionMenuService.currentActionCount;
  mobileSheetHeight = this.defaultMobileSheetHeight;

  constructor(
    public adminActionMenuService: AdminActionMenuService,
    public adminNotesFabService: AdminNotesFabService,
    private toastService: ToastService,
    private elementRef: ElementRef<HTMLElement>,
    private userRolesService: UserRolesService,
    private appVersionService: AppVersionService,
  ) {
    void this.appVersionService.load();

    effect(() => {
      if (!this.adminActionMenuService.hasVisibleActions()) {
        this.close();
      }
    }, { allowSignalWrites: true });
  }

  get context(): AdminActionMenuContext | null {
    return this.adminActionMenuService.currentContext();
  }

  get canUseAdminTools(): boolean {
    return this.userRolesService.isSystemAdmin();
  }

  get scopeLabel(): string {
    return this.context?.kind === 'modal' ? 'Modal' : 'Screen';
  }

  get panelTitle(): string {
    return this.context?.title || this.context?.contextLabel || 'Admin tools';
  }

  get panelSubject(): string | null {
    if (!this.context?.contextLabel || !this.context?.title) {
      return null;
    }

    return this.context.contextLabel === this.context.title ? null : this.context.contextLabel;
  }

  get mobileSheetHeightStyle(): string {
    return `${this.mobileSheetHeight}dvh`;
  }

  get notesBadgeLabel(): string {
    const count = this.adminNotesFabService.unresolvedCount();
    return count > 99 ? '99+' : `${count}`;
  }

  openNotesModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.adminNotesFabService.requestOpen();
  }

  handleToggleClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  toggle(): void {
    if (!this.canUseAdminTools || !this.adminActionMenuService.hasVisibleActions()) {
      return;
    }

    if (!this.adminActionMenuService.isOpen()) {
      this.loadSavedMobileSheetHeight();
    }

    this.adminActionMenuService.toggle();
  }

  close(): void {
    // The result of a build check is only meaningful for the visit that ran it.
    this.versionCheck.set('idle');
    this.persistMobileSheetHeight();
    this.stopMobileResize();
    this.adminActionMenuService.close();
  }

  get buildEnvironment(): string | null {
    return this.appVersionService.buildInfo()?.environment ?? null;
  }

  /** Anything but production is worth flagging: it explains a lot of "bugs". */
  get isProductionBuild(): boolean {
    return this.buildEnvironment === 'prd';
  }

  get buildCommit(): string | null {
    return this.appVersionService.buildInfo()?.commit ?? null;
  }

  get buildBranch(): string | null {
    return this.appVersionService.buildInfo()?.branch ?? null;
  }

  get buildTimeLabel(): string | null {
    return this.appVersionService.buildTimeLabel();
  }

  get buildAge(): string | null {
    return this.appVersionService.buildAge();
  }

  get hasBuildInfo(): boolean {
    return !!this.appVersionService.buildInfo();
  }

  get versionCheckMessage(): string | null {
    switch (this.versionCheck()) {
      case 'current':
        return 'This tab is running the deployed build.';
      case 'stale':
        return 'A newer build has been deployed.';
      case 'error':
        return 'Could not reach the deployed version file.';
      default:
        return null;
    }
  }

  /** Puts the running build on the clipboard so it can be pasted into a report. */
  async copyBuildDetails(): Promise<void> {
    const details = this.appVersionService.versionDetails();

    if (!(await this.writeToClipboard(details))) {
      // The toast renders as plain text, so the copy fallback has to be one line.
      this.toastService.show(details.split('\n').join(' · '), {
        classname: 'bg-primary-subtle text-dark',
        header: 'Frontend version',
        delay: 8000,
      });
      return;
    }

    this.toastService.show('Build details copied to clipboard.', {
      classname: 'bg-primary-subtle text-dark',
      header: 'Copied',
      delay: 2500,
    });
  }

  async checkForNewerBuild(): Promise<void> {
    if (this.versionCheck() === 'checking') {
      return;
    }

    this.versionCheck.set('checking');
    this.versionCheck.set(await this.appVersionService.checkForNewerBuild());
  }

  reloadForNewerBuild(): void {
    window.location.reload();
  }

  private async writeToClipboard(value: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch {
      // Clipboard access can be refused; the toast above still shows the details.
    }

    return false;
  }

  isActionDisabled(action: AdminActionMenuAction): boolean {
    return !this.canUseAdminTools || !!action.disabled || action.badge === 'Soon';
  }

  startMobileResize(event: PointerEvent): void {
    if (!this.isMobileViewport()) {
      return;
    }

    this.resizePointerId = event.pointerId;
    this.resizeStartY = event.clientY;
    this.resizeStartHeight = this.mobileSheetHeight;

    event.preventDefault();
    event.stopPropagation();
  }

  handlePanelClick(event: Event): void {
    event.stopPropagation();
  }

  async runAction(action: AdminActionMenuAction): Promise<void> {
    if (!this.canUseAdminTools || this.isActionDisabled(action)) {
      return;
    }

    if (!action.onSelect) {
      this.toastService.showInfo(`${action.label} is ready to be wired in.`, {
        delay: 3500,
      });
      return;
    }

    try {
      await Promise.resolve(action.onSelect());

      if (action.closeOnSelect !== false) {
        this.close();
      }
    } catch (error) {
      console.error('Admin action failed:', error);
      this.toastService.showError(`Could not run ${action.label.toLowerCase()}.`, {
        delay: 4000,
      });
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.adminActionMenuService.isOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.close();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.adminActionMenuService.isOpen()) {
      this.close();
    }
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent): void {
    if (this.resizePointerId !== event.pointerId || !this.isMobileViewport()) {
      return;
    }

    const viewportHeight = window.innerHeight || 1;
    const deltaY = this.resizeStartY - event.clientY;
    const deltaHeight = (deltaY / viewportHeight) * 100;
    this.mobileSheetHeight = this.clampMobileSheetHeight(this.resizeStartHeight + deltaHeight);

    event.preventDefault();
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  handlePointerRelease(event: PointerEvent): void {
    if (this.resizePointerId !== event.pointerId) {
      return;
    }

    this.mobileSheetHeight = this.snapMobileSheetHeight(this.mobileSheetHeight);
    this.persistMobileSheetHeight();
    this.stopMobileResize();
  }

  private stopMobileResize(): void {
    this.resizePointerId = null;
  }

  private resetMobileSheetHeight(): void {
    this.mobileSheetHeight = this.defaultMobileSheetHeight;
  }

  private loadSavedMobileSheetHeight(): void {
    if (typeof window === 'undefined') {
      this.resetMobileSheetHeight();
      return;
    }

    const savedValue = window.localStorage.getItem(this.mobileSheetHeightStorageKey);
    if (!savedValue) {
      this.resetMobileSheetHeight();
      return;
    }

    const parsedValue = Number.parseFloat(savedValue);
    this.mobileSheetHeight = Number.isFinite(parsedValue)
      ? this.snapMobileSheetHeight(parsedValue)
      : this.defaultMobileSheetHeight;
  }

  private persistMobileSheetHeight(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.mobileSheetHeightStorageKey, this.mobileSheetHeight.toString());
  }

  private clampMobileSheetHeight(value: number): number {
    return Math.min(this.maxMobileSheetHeight, Math.max(this.minMobileSheetHeight, value));
  }

  private snapMobileSheetHeight(value: number): number {
    const clampedValue = this.clampMobileSheetHeight(value);

    return this.mobileSheetSnapPoints.reduce((closestPoint, currentPoint) => {
      return Math.abs(currentPoint - clampedValue) < Math.abs(closestPoint - clampedValue)
        ? currentPoint
        : closestPoint;
    }, this.mobileSheetSnapPoints[0]);
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 767.98;
  }
}
